import { useMemo } from "react"
import useSWR from "swr"
import type { SWRConfiguration } from "swr"
import { BASE_URL } from "../config"

export type GeoJsonFeature = {
  type: 'Feature'
  id?: string | number
  geometry: { type: string; coordinates: [number, number] }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties: Record<string, any>
}

export type GeoJsonFeatureCollection = {
  type: 'FeatureCollection'
  features: GeoJsonFeature[]
}

// export type GeoJsonFeatureMap = Record<string, GeoJsonFeature>;


/* -------------------------------------------------------------------------- */
/*                                   Config                                   */
/* -------------------------------------------------------------------------- */

export const NYC_BOUNDS = {
  south: 40.477399,
  west: -74.25909,
  north: 40.917577,
  east: -73.700272,
}
// const BASE_URL = import.meta.env.VITE_OGC_FEATURES_BASE || 'https://gis.floodnet.nyc/features';
// const BASE_URL = import.meta.env.VITE_OGC_FEATURES_BASE || 'https://gis.dev.floodlabs.nyc/features';
// const BASE_URL = import.meta.env.VITE_OGC_FEATURES_BASE || 'https://features.dev.floodlabs.nyc/features';

/* -------------------------------------------------------------------------- */
/*                                    Utils                                   */
/* -------------------------------------------------------------------------- */

export function toDateInputLocal(d: Date) {
  // returns ISO-like string compatible with input[type=datetime-local]
  const pad = (n: number) => String(n).padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

const encodeURLSearchParams = (params: Record<string, string | number | undefined>) => {
  return new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]) as [string, string][]).toString()
}

/* -------------------------------------------------------------------------- */
/*                                  URL Types                                 */
/* -------------------------------------------------------------------------- */

export function buildOGCCollectionsUrl(collection: string, params: Record<string, string | number | undefined> = {}) {
  return collection ? `${BASE_URL}/collections/${collection}/items?${encodeURLSearchParams(params)}` : null
}

export function buildOGCFunctionUrl(func: string, params: Record<string, string | number | undefined> = {}) {
  return func ? `${BASE_URL}/functions/${func}/items?${encodeURLSearchParams(params)}` : null;
}


/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */

const geojsonfetcher = (url: string) => {
  // console.log(url)
  return fetch(url).then(res => res.json()).then(json => {
    // console.log(url, json)
    return (json?.features ?? []) as GeoJsonFeature[]
  });
}

export function useOGCCollection(
  collection: string,
  params: Record<string, string | number | undefined> = {},
  transform?: (data: GeoJsonFeature) => GeoJsonFeature,
  swrOptions?: SWRConfiguration
) {
  const { data, error, isLoading } = useSWR(
    buildOGCCollectionsUrl(collection, { limit: 10000, ...params }),
    geojsonfetcher,
    { revalidateOnFocus: false, keepPreviousData: true, ...(swrOptions || {}) }
  );
  const features = useMemo(() => (data && transform ? data.map(transform) : data), [data, transform]);
  return { features, loading: isLoading, error }
}

export function useOGCFunction(func: string, params: Record<string, string | number | undefined> = {}) {
  const { data, error, isLoading } = useSWR(buildOGCFunctionUrl(func, params), geojsonfetcher, { revalidateOnFocus: false, keepPreviousData: true })
  return { features: data as GeoJsonFeature[] | undefined, loading: isLoading, error }
}

const jsonfetcher = (url: string) => {
  // console.log(url)
  return fetch(url).then(res => res.json()).then(json => {
    // console.log(url, json)
    return (json ?? []) as OGCJsonProperties[]
  });
}

type OGCJsonProperties = Record<string, string | number | undefined>;
export function useOGCJsonCollection(collection: string, params: Record<string, string | number | undefined> = {}, transform?: (data: OGCJsonProperties) => OGCJsonProperties) {
  const { data, error, isLoading } = useSWR(buildOGCCollectionsUrl(collection, { limit: 10000, ...params }), jsonfetcher, { revalidateOnFocus: false, keepPreviousData: true })
  const features = useMemo(() => data && transform ? data.map(transform) : data, [data, transform]);
  return { features, loading: isLoading, error }
}

export function useOGCJsonFunction(func: string, params: Record<string, string | number | undefined> = {}, transform?: (data: OGCJsonProperties) => OGCJsonProperties) {
  const { data, error, isLoading } = useSWR(buildOGCFunctionUrl(func, params), jsonfetcher, { revalidateOnFocus: false, keepPreviousData: true })
  const features = useMemo(() => data && transform ? data.map(transform) : data, [data, transform]);
  return { features, loading: isLoading, error }
}






export const useClosestTimeDeltas = (features: GeoJsonFeature[] | undefined, currentTime: Date | undefined) => {
  const t0 = currentTime?.getTime() || 0;
  const closestTimeDelta = useMemo(() => {
    if (!features || !t0) return {};
    const result: Record<string, number> = {};
    for (const f of features) {
      const k = f.id!.toString();
      const t = f.properties.time.getTime();
      const dt = t0 - t;
      if (dt >= 0 && (!result[k] || dt < result[k])) {
        result[k] = dt;
      }
    }
    return result;
  }, [features, t0]);
  return closestTimeDelta;
}

export type ClosestTimeResult = {
  [key: string]: {
    delta: number;
    feature: GeoJsonFeature;
    t: number;
  }
};
export const useClosestTime = (features: GeoJsonFeature[] | undefined, currentTime: Date | undefined) => {
  const t0 = currentTime?.getTime() || 0;
  const closestTime = useMemo(() => {
    if (!features || !t0) return {} as ClosestTimeResult;
    const result: ClosestTimeResult = {};
    for (const f of features) {
      const k = f.id!.toString();
      const t = f.properties.time.getTime();
      const dt = t0 - t;
      if (dt >= 0 && (!result[k] || dt < result[k].delta) && dt < 60 * 60 * 1000) {
        result[k] = { delta: dt, feature: f, t };
      }
    }
    return result;
  }, [features, t0]);
  return closestTime;
}
